import OrgNumberInput from "./OrgNumberInput";
import CaseCreateButton from "./CaseCreateButton";
import { Box, CircularProgress, TextField } from "@mui/material";
import { Outlet, useLoaderData, useNavigation, useLocation } from "react-router-dom";
import { RuleSet } from "../../types/ruleSetTypes";
import { CaseListEntry } from "./types";
import { OrgGraphVisRef } from "org-graph/OrgGraphVis";
import React from "react";
import { useAuth } from "react-oidc-context";
import { getCognitoGroups } from "../../auth/authUtils";
import api from "../../lib/api";
import { useTranslation } from 'react-i18next';

// Define the context type for the Outlet
export interface CompanyAssessmentOutletContext {
  orgGraphRef: React.RefObject<OrgGraphVisRef>;
  isFullscreen: boolean;
  setIsFullscreen: (isFullscreen: boolean) => void;
  topBarContent?: React.ReactNode;
}

// This fetches the ruleset names to display in the dropdown
export async function companyAssessmentRuleSetLoader() {
  return api.get<RuleSet[]>('/v1/rulesets');
}

export default function CompanyAssessment() {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const location = useLocation();
  const ruleSets = useLoaderData() as RuleSet[];
  
  // Create ref for OrgGraphVis
  const orgGraphRef = React.useRef<OrgGraphVisRef>(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  // State for cases list and case name when case is selected
  const [cases, setCases] = React.useState<CaseListEntry[]>([]);
  const [fetchingCases, setFetchingCases] = React.useState<boolean>(false);

  const auth = useAuth();

  // Memoize URL parsing to prevent unnecessary re-renders
  const urlParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const currentOrgNumber = React.useMemo(() => location.pathname.split('/').pop(), [location.pathname]);
  const caseIdFromUrl = urlParams.get('caseId');
  
  // Show Save Case button if:
  // 1. We have a current org number in the URL (assessment is loaded)
  // 2. Navigation is not in loading state (assessment has actually finished loading)
  // 3. Current org number is valid (not just the base route)
  const isValidOrgNumber = currentOrgNumber && currentOrgNumber !== 'company-assessment' && /^\d+$/.test(currentOrgNumber);
  const showSaveCaseButton = !!(isValidOrgNumber && navigation.state === 'idle');

  const isLoading = navigation.state === 'loading';

  const hasCasesFeature = React.useMemo(() => {
    const token = auth?.user?.access_token;
    if (token) {
      const groups = getCognitoGroups(token);
      return groups.includes('Feature_Cases');
    }
    return false;
  }, [auth?.user?.access_token]);

  // Fetch cases list when component mounts or auth changes
  React.useEffect(() => {
    const fetchCases = async () => {
      const token = auth?.user?.access_token;
      if (!token) {
        return;
      }
      
      const groups = getCognitoGroups(token);
      if (!groups.includes('Feature_Cases')) {
        return;
      }

      setFetchingCases(true);
      try {
        const casesData = await api.get<CaseListEntry[]>('/v1/cases');
        setCases(casesData);
      } catch (err) {
        console.error('Error fetching cases:', err);
        setCases([]);
      } finally {
        setFetchingCases(false);
      }
    };

    fetchCases();
  }, [auth?.user?.access_token]);

  // Find the current case by ID
  const currentCase = React.useMemo(() => {
    return caseIdFromUrl ? cases.find(c => c.id === caseIdFromUrl) : null;
  }, [caseIdFromUrl, cases]);

  const topBarContent = React.useMemo(() => (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {!caseIdFromUrl && (
        <OrgNumberInput 
          ruleSets={ruleSets}
        />
      )}
      {caseIdFromUrl && (
        <TextField
          size="small"
          label={t('assessment.cases.name')}
          value={fetchingCases ? t('common.states.loading') : (currentCase?.name || t('assessment.details.caseNotFound'))}
          disabled={true}
          sx={{ 
            minWidth: 300,
            '& .MuiInputBase-input.Mui-disabled': {
              color: 'text.primary',
              WebkitTextFillColor: 'text.primary'
            }
          }}
        />
      )}
      {showSaveCaseButton && hasCasesFeature && (
        <CaseCreateButton
          disabled={isLoading}
          orgNumber={currentOrgNumber || ''}
          orgGraphRef={orgGraphRef}
          currentCaseId={caseIdFromUrl}
          currentCaseName={currentCase?.name || null}
        />
      )}
      {isLoading && (
        <CircularProgress size={36} />
      )}
    </Box>
  ), [caseIdFromUrl, ruleSets, fetchingCases, currentCase, showSaveCaseButton, hasCasesFeature, isLoading, currentOrgNumber, orgGraphRef, t]);

  // Memoize outlet context to prevent unnecessary re-renders
  const outletContext = React.useMemo(() => ({
    orgGraphRef,
    isFullscreen,
    setIsFullscreen,
    topBarContent
  }), [orgGraphRef, isFullscreen, topBarContent]);

  return (
    <Box sx={{ height: '100vh', width: '100%', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ width: '100%', height: '100%' }}>
        <Outlet context={outletContext} />
      </Box>
    </Box>
  );
};
